/**
 * Connect6 盤 (19x19) コンポーネント。
 *
 * - 交点に石を置く (Renju と同じ描画モデル)。 SVG で 18x18 罫線 + 5 つの星。
 * - 黒先 1 石、 以降 1 ターン 2 石。 1 石目を打った直後は `pending_pair_first` が
 *   立った状態で同 side の手番が続く。 UI は 1 石目の位置にだけ別ハイライト
 *   (`pending-first`) を出して、 「次は 2 石目」 を可視化する。
 * - last-move ハイライト: 1 ターン 2 石なので、 ターンが完了した局面 (= pending 解消)
 *   では直近 2 手 (lastMove + prevMove) を両方ハイライトする。 初手 (盤上 1 石のみ)
 *   と pair 進行中 (pending 中) は 1 石だけ。
 * - 6 連以上で勝ち、 引き分けは盤満杯。
 */

import {
  CONNECT6_SIZE,
  type Connect6Move,
  type Connect6State,
  isDrawOutcome,
  isPlace,
  isWinOutcome,
  legalKey,
  type Side,
} from '../../games/connect6';
import { usePendingConfirm } from '../../hooks/usePendingConfirm';
import { useFreshLastMove } from '../../lib/first-paint';
import { BoardCoords } from './BoardCoords';
import { QuietPlacementTools } from './QuietPlacementTools';

interface Props {
  state: Connect6State;
  legal?: Connect6Move[];
  yourSide?: Side | null;
  myTurnAllowed?: boolean;
  onMove?: (mv: Connect6Move) => void;
  mini?: boolean;
  lastMove?: Connect6Move;
  /** 直近の 1 つ前の手。 1 ターン 2 石を両方ハイライトするのに使う。 */
  prevMove?: Connect6Move;
  /** Replay / Archive 等の静的ビューでは「相手の手番」 表示を出さない。 */
  staticView?: boolean;
  hideOwnSide?: boolean;
  /** チュートリアル用の交点強調。 */
  tutorialHighlight?: [number, number][];
}

const N = CONNECT6_SIZE; // 19

/// 連珠と同様の星 / 天元配置。 19x19 の伝統的な囲碁星位 9 点を流用する。
const STAR_POINTS: [number, number][] = [
  [3, 3],
  [3, 9],
  [3, 15],
  [9, 3],
  [9, 9],
  [9, 15],
  [15, 3],
  [15, 9],
  [15, 15],
];

export function Connect6Board({
  state,
  legal,
  yourSide,
  myTurnAllowed,
  onMove,
  mini,
  lastMove,
  prevMove,
  staticView,
  tutorialHighlight,
}: Props) {
  // 盤を開いた瞬間は直前手のリングを出さない (mount 後の新しい手だけアニメする)。
  const freshLast = useFreshLastMove(lastMove);
  const isMyTurn =
    myTurnAllowed !== undefined ? myTurnAllowed : yourSide != null && yourSide === state.side;
  const legalSet = new Set(
    (legal ?? []).filter(isPlace).map((m) => legalKey(m.Place.row, m.Place.col)),
  );
  // 2 タップ確定 (1 回目: 半透明 pending、 2 回目: commit)
  const { pending: pendingTap, tap: handleTap } = usePendingConfirm<string>(state);
  // pending の色 = 自分の色 (yourSide が null なら state.side)
  const pendingColor: 'First' | 'Second' = (yourSide ?? state.side) as 'First' | 'Second';
  // last-move ハイライト対象。 ターン完了局面 (pending 解消) では直近 2 手を両方含める。
  // pair 進行中 (pending 中) と初手 (prevMove 無し) は lastMove 1 石のみ。
  const lastKeys = new Set<string>();
  if (lastMove && isPlace(lastMove)) {
    lastKeys.add(legalKey(lastMove.Place.row, lastMove.Place.col));
    if (state.pending_pair_first == null && prevMove && isPlace(prevMove)) {
      lastKeys.add(legalKey(prevMove.Place.row, prevMove.Place.col));
    }
  }
  const pendingKey = state.pending_pair_first
    ? legalKey(state.pending_pair_first[0], state.pending_pair_first[1])
    : null;

  const showOppHint = !mini && !staticView && !isMyTurn && state.outcome == null;

  const pctOf = (i: number) => (i / (N - 1)) * 100;

  // pair 進行状況のフッタ表示: 「次は 2 石目」 を明示。
  const pendingLabel = !mini && !staticView && state.pending_pair_first
    ? '同じ手番がもう 1 石置けます (1 ターン 2 石)'
    : null;

  return (
    <div className={`connect6-frame ${mini ? 'mini' : ''}`}>
      <QuietPlacementTools game="Connect6" yourSide={yourSide} mini={mini} staticView={staticView} />
      <div className={`connect6-board ${mini ? 'mini' : ''} ${showOppHint ? 'not-my-turn' : ''}`}>
        <div className="connect6-grid-area">
          {/* 座標は盤線の外 (木地) に。 交点の中に置くと石で隠れる。 */}
          {!mini && (
            <BoardCoords
              variant="intersection"
              files={Array.from({ length: N }, (_, c) => String.fromCharCode(65 + c))}
              ranks={Array.from({ length: N }, (_, r) => `${N - r}`)}
            />
          )}
          {/* 罫線: 18×18 のマスを引く = 19×19 交点 */}
          <svg
            className="connect6-grid-svg"
            viewBox={`0 0 ${N - 1} ${N - 1}`}
            preserveAspectRatio="none"
            aria-hidden
          >
            {Array.from({ length: N }, (_, i) => (
              <g key={`l${i}`}>
                <line x1={0} y1={i} x2={N - 1} y2={i} stroke="#1a1a1a" strokeWidth="0.04" />
                <line x1={i} y1={0} x2={i} y2={N - 1} stroke="#1a1a1a" strokeWidth="0.04" />
              </g>
            ))}
            {STAR_POINTS.map(([r, c]) => (
              <circle key={`s${r}-${c}`} cx={c} cy={r} r={0.16} fill="#1a1a1a" />
            ))}
          </svg>

          {state.board.flatMap((row, r) =>
            row.map((cell, c) => {
              const k = legalKey(r, c);
              const isLegal = isMyTurn && legalSet.has(k);
              const isLast = !mini && lastKeys.has(k);
              const isPending = !mini && pendingKey === k;
              const isPendingTap = !mini && pendingTap === k;
              const isTutorialHint =
                tutorialHighlight?.some(([hr, hc]) => hr === r && hc === c) ?? false;
              const handler = () => {
                handleTap(k, isLegal, () => onMove?.({ Place: { row: r, col: c } }));
              };
              const stoneClass =
                cell === 'First' ? 'black' : cell === 'Second' ? 'white' : '';
              // pending-tap セルは自分の色の半透明石を被せる
              const pendingTapClass = isPendingTap
                ? pendingColor === 'First' ? 'pending-tap-black' : 'pending-tap-white'
                : '';
              return (
                <button
                  key={`${r}-${c}`}
                  className={`connect6-intersection ${stoneClass} ${isLegal ? 'legal' : ''} ${isLast ? 'last-move' : ''} ${isLast && !freshLast ? 'no-anim' : ''} ${isPending ? 'pending-first' : ''} ${pendingTapClass} ${isTutorialHint ? 'tutorial-hint' : ''}`}
                  style={{
                    left: `${pctOf(c)}%`,
                    top: `${pctOf(r)}%`,
                  }}
                  onClick={handler}
                  disabled={mini || !isLegal}
                  aria-label={`row ${r} col ${c}`}
                >
                  {cell != null && <span key={cell} className="stone" />}
                  {isPendingTap && cell == null && <span className="pending-stone" aria-hidden />}
                </button>
              );
            }),
          )}
        </div>
      </div>
      {showOppHint && (
        <div className="board-turn-hint" aria-hidden>
          手番待ち
        </div>
      )}
      {pendingLabel && (
        <div className="connect6-pair-hint" aria-live="polite">
          {pendingLabel}
        </div>
      )}
      {!mini && isWinOutcome(state.outcome) && (
        <div className="connect6-outcome-hint">
          {state.outcome.Win.winner === 'First' ? '黒' : '白'} の 6 連 — 勝利
        </div>
      )}
      {!mini && isDrawOutcome(state.outcome) && (
        <div className="connect6-outcome-hint">盤満杯 — 引き分け</div>
      )}
    </div>
  );
}

export const CONNECT6_BOARD_SIZE = CONNECT6_SIZE;
